import { OptionsFill24Icon } from "@vygruppen/spor-icon-react";
import { ComponentProps, FC, Fragment, ReactNode } from "react";
import {
  Button,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  tokens,
} from "shared/components/Spor";
import styled from "styled-components";

export type PanelAction = {
  key: string;
  label: string;
  leftIcon?: ReactNode;
  buttonVariant?: ComponentProps<typeof Button>["variant"];
  buttonSize?: ComponentProps<typeof Button>["size"];
  buttonTitle?: string;
  onSelect: () => void;
  modal: ReactNode;
};

/**
 * Only visible on small panel widths, collapses actions into the overflow menu.
 */
const OverflowOnly = styled.div`
  @container altTransportPage (width >= 1696px) {
    display: none;
  }
`;

/**
 * Only visible on wide panel widths, shows actions inline.
 */
const InlineActions = styled.div`
  display: none;

  @container altTransportPage (width >= 1696px) {
    display: flex;
    flex-direction: row;
    gap: ${tokens.default.size.spacing[2]};
  }
`;

const IconMenuTrigger = styled(MenuTrigger)`
  && {
    width: 40px;
    min-width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 50%;
  }
`;

type ActionsOverflowMenuProps = {
  actions: PanelAction[];
};

/**
 * Triple-dot menu in upper corner shown on small widths.
 */
const ActionsOverflowMenu: FC<ActionsOverflowMenuProps> = ({ actions }) => {
  if (actions.length === 0) {
    return null;
  }

  return (
    <>
      <OverflowOnly>
        <Menu positioning={{ placement: "bottom-end" }}>
          <IconMenuTrigger
            variant="tertiary"
            size="sm"
            aria-label="Flere valg"
            withChevron={false}
            icon={<OptionsFill24Icon />}
          />
          <MenuContent>
            {actions.map((action) => (
              <MenuItem
                key={action.key}
                value={action.key}
                leftIcon={action.leftIcon}
                onClick={action.onSelect}
              >
                {action.label}
              </MenuItem>
            ))}
          </MenuContent>
        </Menu>
      </OverflowOnly>
      {actions.map((action) => (
        <Fragment key={action.key}>{action.modal}</Fragment>
      ))}
    </>
  );
};

/**
 * Inline (wide layout) button for a single action.
 */
const InlineActionButton: FC<{ action: PanelAction }> = ({ action }) => (
  <Button
    variant={action.buttonVariant ?? "tertiary"}
    size={action.buttonSize ?? "sm"}
    leftIcon={action.leftIcon}
    onClick={action.onSelect}
    title={action.buttonTitle}
    whiteSpace="nowrap"
    width="100%"
  >
    {action.label}
  </Button>
);

type PanelActionsProps = {
  /** Actions collapsed into the overflow menu on small widths. */
  overflowActions: PanelAction[];
  /** Actions shown as inline buttons on large widths */
  inlineActions: PanelAction[];
};

/**
 * Panel header actions: an overflow menu on narrow widths and inline buttons on
 * wide widths. Rendered once
 * regardless of width, so both triggers share the same modal state.
 */
export const PanelActions: FC<PanelActionsProps> = ({
  overflowActions,
  inlineActions,
}) => (
  <>
    <ActionsOverflowMenu actions={overflowActions} />
    <InlineActions>
      {inlineActions.map((action) => (
        <InlineActionButton key={action.key} action={action} />
      ))}
    </InlineActions>
  </>
);
